import apiBase from "./lithic/apiBase";
import axios from "axios";
import config from "./config";

const instance = apiBase.create(config.apiHost, {
  debug: config.debug,
  chaos: config.chaos
});

const get = (path, params, opts) => {
  return instance.get(path, apiBase.mergeParams(params, opts));
};
const post = (path, params, opts) => {
  return instance.post(path, params, opts);
};
const patch = (path, params, opts) => {
  return instance.patch(path, params, opts);
};

const put = (path, params, opts) => {
  return instance.put(path, params, opts);
};

const del = (path, params, opts) => {
  return instance.delete(path, apiBase.mergeParams(params, opts));
};

function followRedirect(history) {
  return function (resp) {
    if (resp.headers["created-resource-admin"])
      history.push(resp.headers["created-resource-admin"]);

    return resp;
  };
}

// Helper to extract data from API responses
const pickData = (response) => {
  return response && response.data;
};

// Bypass apiBase's `humps.camelizeKeys` transform — it corrupts CSV blobs into `[object Object]`.
const BLOB_CONFIG = {
  responseType: "blob",
  transformResponse: axios.defaults.transformResponse
};

const api = {
  followRedirect,
  get,
  post,
  patch,
  put,
  delete: del,
  pickData,
  signOut: () => del("/v1/auth"),
  signIn: (data) => post("/v1/auth", data),

  getCurrentUser: (data) => get("/admin/v1/auth", data),
  // Self-service password change for internal admins (INF-231). humps
  // decamelizes the body to { current_password, new_password } on the wire.
  changePassword: (data) => post("/admin/v1/auth/password", data),
  getCustomers: (data) => get("/admin/v1/customers", data),
  getAffiliates: (data) => get("/admin/v1/affiliates", data),
  getAffiliate: (data) => get(`/admin/v1/affiliates/${data.id}`, data),
  getRentalVerifications: (data) =>
    get(
      `/admin/v1/reporting_account_enrollments/${data.id}/payment_verification`,
      data
    ),
  getPaymentAnalyzerData: (data) =>
    get(
      `/admin/v1/reporting_account_enrollments/${data.id}/payment_analyzer`,
      data
    ),
  getBankTransactions: (data) =>
    get(
      `/admin/v1/reporting_account_enrollments/${data.id}/bank_transactions`,
      data
    ),
  getCustomer: (data) => get(`/admin/v1/customers/${data.id}`, data),
  getCustomerPayments: (data) =>
    get(`/admin/v1/customers/${data.id}/payments`, data),
  getCustomerInvoices: (data) =>
    get(`/admin/v1/customers/${data.id}/invoices`, data),
  getCustomerDocuments: (data) =>
    get(`/admin/v1/customers/${data.id}/documents`),
  getCustomerPaymentInstruments: (data) =>
    get(`/admin/v1/customers/${data.id}/payment_instruments`, data),
  getCustomerBankAccounts: (data) =>
    get(`/admin/v1/customers/${data.id}/bank_accounts`, data),
  updateCustomer: (data) => post(`/admin/v1/customers/${data.id}`, data),
  updateAffiliate: (data) => patch(`/admin/v1/affiliates/${data.id}`, data),
  createAffiliate: (data) => post("/admin/v1/affiliates", data),
  removeAffiliate: (data) => del(`/admin/v1/affiliates/${data.id}`, data),
  closeCustomerAccount: (data) =>
    post(`/admin/v1/customers/${data.id}/close`, data),
  markFraudulent: (data) =>
    post(`/admin/v1/customers/${data.id}/mark_fraudulent`, data),
  unmarkFraudulent: (data) =>
    post(`/admin/v1/customers/${data.id}/unmark_fraudulent`, data),

  addCustomerReferral: (data) =>
    post(`/admin/v1/customers/${data.id}/add_referral`, data),

  getAccountBalances: (data) =>
    get(`/admin/v1/customers/${data.id}/account_balances`, data),

  generateManualOTP: (customerId) =>
    post(`/admin/v1/customers/${customerId}/otp/generate_manual`),

  listPayments: (data) => post("/admin/v1/payments/list", data),
  buildDownloadPaymentsUrl: (data) =>
    post("/admin/v1/payments/list/download.csv", data),
  getPayment: (data) => get(`/admin/v1/payments/${data.id}`, data),
  updatePayment: (data) => patch(`/admin/v1/payments/${data.id}`, data),

  reviewPayment: (data) => post(`/admin/v1/payments/${data.id}/review`, data),
  unreviewPayment: (data) => del(`/admin/v1/payments/${data.id}/review`, data),
  markPaymentStrategyFundsSent: (data) =>
    post(`/admin/v1/payments/${data.id}/strategy/mark_funds_sent`, data),

  listInvoices: (data) => post("/admin/v1/invoices/list", data),
  getInvoice: (data) => get(`/admin/v1/invoices/${data.id}`, data),
  updateInvoice: (data) => patch(`/admin/v1/invoices/${data.id}`, data),

  listPayouts: (data) => post("/admin/v1/payouts/list", data),
  getPayout: (data) => get(`/admin/v1/payouts/${data.id}`, data),
  updatePayout: (data) => patch(`/admin/v1/payouts/${data.id}`, data),

  listStatements: (data) => post("/admin/v1/statements", data),

  // Statement payment method APIs
  updateStatementPaymentMethod: (data) =>
    post(`/admin/v1/statements/${data.id}/update_payment_method`, data),

  payStatements: (data) => post("/admin/v1/statements/process_payments", data),
  markStatementAsPaid: (data) =>
    post(`/admin/v1/statements/${data.id}/mark_as_paid`),

  getStatementAvailablePaymentMethods: (data) =>
    get(`/admin/v1/statements/${data.id}/available_payment_methods`),

  updateEnrollmentCasePortal: (data) =>
    patch(`/admin/v1/enrollment_cases/${data.id}/portal`, data),
  updateEnrollmentCaseLease: (data) =>
    patch(`/admin/v1/enrollment_cases/${data.id}/lease`, data),
  updateEnrollmentCaseLandlord: (data) =>
    patch(`/admin/v1/enrollment_cases/${data.id}/landlord`, data),
  closeEnrollmentCase: (data) =>
    post(`/admin/v1/enrollment_cases/${data.id}/close`, data),

  verifyEnrollmentCaseLandlord: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/verify_landlord_verification`,
      data
    ),
  rejectEnrollmentCaseLandlord: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/reject_landlord_verification`,
      data
    ),
  verifyEnrollmentCaseLease: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/verify_lease_verification`,
      data
    ),
  rejectEnrollmentCaseLease: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/reject_lease_verification`,
      data
    ),
  verifyUploadedLease: (data) =>
    post(`/admin/v1/enrollment_cases/${data.id}/verify_uploaded_lease`, data),
  rejectUploadedLease: (data) =>
    post(`/admin/v1/enrollment_cases/${data.id}/reject_uploaded_lease`, data),
  verifyEnrollmentCasePortal: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/verify_portal_verification`,
      data
    ),
  rejectEnrollmentCasePortal: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/reject_portal_verification`,
      data
    ),
  verifyEnrollmentCaseIdentity: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/verify_identity_verification`,
      data
    ),
  rejectEnrollmentCaseIdentity: (data) =>
    post(
      `/admin/v1/enrollment_cases/${data.id}/reject_identity_verification`,
      data
    ),

  listReportingAccountEnrollments: (data) =>
    post("/admin/v1/reporting_account_enrollments/list", data),
  exportReportingAccountEnrollments: (data) =>
    post("/admin/v1/reporting_account_enrollments/list/export_to_csv", data, {
      responseType: "blob"
    }),
  getReportingAccountEnrollment: (data) =>
    get(`/admin/v1/reporting_account_enrollments/${data.id}`),
  updateReportingAccountEnrollment: (data) =>
    patch(`/admin/v1/reporting_account_enrollments/${data.id}`, data),
  closeReportingAccountEnrollment: (data) =>
    post(`/admin/v1/reporting_account_enrollments/${data.id}/close`, data),
  closeTradelineReportingAccountEnrollment: (data) =>
    post(
      `/admin/v1/reporting_account_enrollments/${data.id}/close_tradeline`,
      data
    ),
  deleteTradelineReportingAccountEnrollment: (data) =>
    post(
      `/admin/v1/reporting_account_enrollments/${data.id}/delete_tradeline`,
      data
    ),
  approveReportingAccountEnrollment: (data) =>
    post(`/admin/v1/reporting_account_enrollments/${data.id}/approve`, data),
  rejectReportingAccountEnrollment: (data) =>
    post(`/admin/v1/reporting_account_enrollments/${data.id}/reject`, data),
  reconsiderReportingAccountEnrollment: (data) =>
    post(`/admin/v1/reporting_account_enrollments/${data.id}/reconsider`, data),
  unenrollOngoing: (data) =>
    post(
      `/admin/v1/reporting_account_enrollments/${data.id}/unenroll_ongoing`,
      data
    ),
  reverseUnenrollOngoing: (data) =>
    post(
      `/admin/v1/reporting_account_enrollments/${data.id}/reverse_unenroll`,
      data
    ),

  getReportingAccountEnrollmentRentalVerifications: (data) =>
    get(
      `/admin/v1/reporting_account_enrollments/${data.id}/rental_verifications`
    ),
  createReportingAccountEnrollmentRentalVerification: (data) =>
    post(
      `/admin/v1/reporting_account_enrollments/${data.id}/rental_verifications`,
      data
    ),
  getMetro2Files: (data) =>
    get(
      `/admin/v1/reporting_account_enrollments/${data.id}/metro2_files`,
      data
    ),
  listRentalVerifications: (data) =>
    post("/admin/v1/enrollment_verifications/list", data),
  getRentalVerification: (data) =>
    get(`/admin/v1/rental_verifications/${data.id}`),
  updateRentalVerification: (data) =>
    patch(`/admin/v1/rental_verifications/${data.id}`, data),
  verifyRentPayment: (data) =>
    post(`/admin/v1/rental_verifications/${data.id}/approve`, data),
  analyzeRentalVerificationTransactions: (data) =>
    post("/admin/v1/rental_verification_analysis", data),
  rejectRentPayment: (data) =>
    post(`/admin/v1/rental_verifications/${data.id}/reject`, data),
  reverifyRentalVerification: (data) =>
    post(`/admin/v1/rental_verifications/${data.id}/reverify`, data),
  linkTransaction: ({ id }, payload = {}) =>
    post(`/admin/v1/rental_verifications/${id}/link_transaction`, payload),
  unlinkTransaction: ({ id }, payload = {}) =>
    post(`/admin/v1/rental_verifications/${id}/unlink_transaction`, payload),
  removeTransactionSource: (data) =>
    post(`/admin/v1/rental_verifications/${data.id}/remove_source`, data),
  approveRentalVerifications: (data) =>
    post("/admin/v1/rental_verifications/bulk_approve", data),
  rejectRentalVerifications: (data) =>
    post("/admin/v1/rental_verifications/bulk_reject", data),
  getBankAccount: (data) => get(`/admin/v1/bank_accounts/${data.id}`, data),
  getCard: (data) => get(`/admin/v1/cards/${data.id}`, data),
  createPresignedDocumentUrl: (data) =>
    post(
      `/admin/v1/customers/${data.customerId}/documents/presign_put_url?filename=${data.filename}`
    ),
  createDocument: (data) =>
    post(`/admin/v1/customers/${data.customerId}/documents/`, data),

  getPartners: (data) => get("/admin/v1/partners", data),
  getPartner: (data) => get(`/admin/v1/partners/${data.id}`, data),
  createPartner: (data) => post(`/admin/v1/partners/`, data),
  getPartnerInvoices: (data) =>
    get(`/admin/v1/partners/${data.id}/invoices`, data),
  getPartnerPayouts: (data) =>
    get(`/admin/v1/partners/${data.id}/payouts`, data),
  getPartnerStatements: (data) => get(`/admin/v1/statements`, data),
  getPartnerUpcomingStatements: (data) =>
    get(`/admin/v1/partners/${data.id}/statements/upcoming`, data),
  getPartnerStatement: (data) =>
    get(`/admin/v1/statements/${data.statement_id}`, data),
  getPartnerStatementActivities: (data) =>
    get(`/admin/v1/statements/${data.statement_id}/activities`, data),
  getPartnerPaymentInstruments: (data) =>
    get(`/admin/v1/partners/${data.id}/payment_instruments`, data),
  getPartnerUnits: (data) => get(`/admin/v1/partners/${data.id}/units`, data),
  getPartnerWebhookRequests: (data) =>
    get(`/admin/v1/partners/${data.partnerId}/webhook_requests`, data),
  getPartnerBillingConfigurations: (data) =>
    get(`/admin/v1/partners/${data.id}/billing_configurations`, data),
  updatePartner: (data) => patch(`/admin/v1/partners/${data.id}`, data),
  addPartnerOneOffCharge: (data) =>
    post(`/admin/v1/partners/${data.id}/add_one_off_charge`, data),
  getPartnerRecurringCharges: (data) =>
    get(`/admin/v1/partners/${data.id}/recurring_charges`),
  createPartnerRecurringCharge: (data) =>
    post(`/admin/v1/partners/${data.id}/recurring_charges`, data),
  updatePartnerRecurringCharge: (data) =>
    patch(
      `/admin/v1/partners/${data.id}/recurring_charges/${data.chargeId}`,
      data
    ),
  deletePartnerRecurringCharge: (data) =>
    del(`/admin/v1/partners/${data.id}/recurring_charges/${data.chargeId}`),
  createPricingTier: (data) =>
    post(`/admin/v1/partners/${data.partner_id}/pricing_tiers`, data),
  updatePricingTier: (data) =>
    patch(
      `/admin/v1/partners/${data.partner_id}/pricing_tiers/${data.tier_id}`,
      data
    ),
  createVerificationPrices: (data) =>
    post(`/admin/v1/partners/${data.partner_id}/verification_prices`, data),
  updateVerificationPrices: (data) =>
    patch(
      `/admin/v1/partners/${data.partner_id}/verification_prices/${data.price_id}`,
      data
    ),

  // Entrata PMS specific endpoints
  downloadEntrataRentersCsv: (data) => {
    const { partnerId, ...queryParams } = data;
    return get(
      `/admin/v1/partners/${partnerId}/entrata_renters_csv`,
      queryParams,
      BLOB_CONFIG
    );
  },
  postRentReportingCharges: (data) => {
    const { partnerId, ...requestData } = data;
    return post(
      `/admin/v1/partners/${partnerId}/post_rent_reporting_charges`,
      requestData
    );
  },

  // Furnishments
  listFurnishments: (data) => get("/admin/v1/furnishments", data),

  getFurnishment: (data) => get(`/admin/v1/furnishments/${data.id}`),

  getReportingAccountEnrollmentsByFurnishment: (data) =>
    post(
      `/admin/v1/furnishments/${data.furnishment_id}/reporting_account_enrollments`,
      data
    ),

  createReportingAccountEnrollmentRefurnishment: (data) =>
    post(
      `/admin/v1/reporting_account_enrollments/${data.id}/refurnishments`,
      data
    ),

  addDocumentForReportingAccountEnrollment: (data) =>
    post(`/admin/v1/enrollment_cases/${data.id}/add_document`, data),

  getReportingAccountEnrollmentFurnishments: (data) =>
    get(`/admin/v1/reporting_account_enrollments/${data.id}/furnishments`),

  getCustomerSubscriptions: (data) =>
    get(`/admin/v1/customers/${data.customerId}/subscriptions`),

  updateCustomerSubscription: (data) =>
    post(
      `/admin/v1/customers/${data.customerId}/subscriptions/${data.subscriptionId}/toggle_auto_renew`
    ),

  renewCustomerSubscription: (data) =>
    post(`/admin/v1/customers/${data.customerId}/subscriptions/renew`, {
      months: data.renewalPeriod
    }),

  cancelCustomerSubscription: (data) =>
    post(
      `/admin/v1/customers/${data.customerId}/subscriptions/${data.subscriptionId}/cancel`
    ),

  refundLineItems: (data) =>
    post(`/admin/v1/invoices/${data.invoiceId}/refund`, data),
  getAuditLogs: (auditable_id, auditable_type) =>
    get(`/admin/v1/audit_logs`, { auditable_id, auditable_type }),
  getEnrollmentVerification: (id) =>
    get(`/admin/v1/enrollment_verifications/${id}`),
  listEnrollmentVerificationsForEnrollment: (enrollmentId) =>
    get(
      `/admin/v1/reporting_account_enrollments/${enrollmentId}/verifications`
    ),

  getFilesForEnrollment: (id) =>
    get(`/admin/v1/reporting_account_enrollments/${id}/files`),
  postUpdateCheck: (enrollmentVerificationId, check) =>
    post(
      `/admin/v1/enrollment_verifications/${enrollmentVerificationId}/update_check`,
      {
        check
      }
    ),
  postApproveEnrollmentVerification: (id, memo) =>
    post(`/admin/v1/enrollment_verifications/${id}/approve`, { memo }),
  postRejectEnrollmentVerification: (id, memo) =>
    post(`/admin/v1/enrollment_verifications/${id}/reject`, { memo }),
  restartEnrollmentVerification: (id) =>
    post(`/admin/v1/enrollment_verifications/${id}/restart`),
  listEnrollmentsReadForPaymentVerification: (data) =>
    post(
      "/admin/v1/reporting_account_enrollments/ready_for_payment_verification",
      data
    ),

  listChimeEnrollments: (data) =>
    post("/admin/v1/reporting_account_enrollments/chime", data),

  getChimeEnrollment: (data) =>
    get(`/admin/v1/reporting_account_enrollments/chime/${data.id}`, data),

  getCustomerTransactions: (data) =>
    get(`/admin/v1/customers/${data.id}/transactions`, data),

  uploadReportsToBureau: (data) =>
    post(
      `/admin/v1/furnishments/${data.id}/reports/${data.reportId}/upload_to_bureau`,
      data
    ),

  generateFurnishment: (data) =>
    post(`/admin/v1/furnishments/generate_furnishment`, data),

  getTradlinesForEnrollment: (enrollmentId) =>
    get(`/admin/v1/tradelines`, { enrollmentId }),
  getTradeline: (tradelineId) => get(`/admin/v1/tradelines/${tradelineId}`),
  syncBureauStatusForTradeline: (tradelineId) =>
    post(`/admin/v1/tradelines/${tradelineId}/sync`),
  updateTradeline: (data) =>
    patch(`/admin/v1/tradelines/${data.tradelineId}`, data),

  getFeedTimeLine: (userId, payload) =>
    get(
      `/admin/v1/reporting_account_enrollments/${userId}/furnishments_timeline`,
      payload
    ),

  listKybVerifications: (data) =>
    post("/admin/v1/kyb_verifications/list", data),
  getKybVerification: (id) => get(`/admin/v1/kyb_verifications/${id}`),
  postKybVerificationUpdateCheck: (kybVerificationId, check) =>
    post(`/admin/v1/kyb_verifications/${kybVerificationId}/update_check`, {
      check
    }),
  postApproveKybVerification: (id, memo) =>
    post(`/admin/v1/kyb_verifications/${id}/approve`, { memo }),
  postRejectKybVerification: (id, memo) =>
    post(`/admin/v1/kyb_verifications/${id}/reject`, { memo }),

  listIdentityVerifications: (data) =>
    post(`/admin/v1/identity_verifications`, data),

  getVerificationSchemas: (data) => get(`/admin/v1/verification_schemas`, data),

  listApplications: (data) => post(`/admin/v1/applications`, data),
  getApplication: (id) => get(`/admin/v1/applications/${id}`),
  updateApplication: (applicationId, data) =>
    patch(`/admin/v1/applications/${applicationId}`, data),
  getApplicant: (id) => get(`/admin/v1/applicants/${id}`),
  getBoomScreenVerification: (id) => get(`/admin/v1/verifications/${id}`),
  presignAsset: (queryParams) => get(`/admin/v1/assets/presign?${queryParams}`),
  createAsset: (data) => post(`/admin/v1/assets`, data),
  setPartnerLogo: (partnerId, data) =>
    post(`/admin/v1/partners/${partnerId}/set_logo`, data),
  impersonatePartnerUser: (partnerId, user) =>
    post(`/admin/v1/partner_users/${user.id}/impersonate`),
  getYardiChargesCsv: (partnerId, queryParams = {}) =>
    get(
      `/admin/v1/partners/${partnerId}/yardi_charges_csv`,
      queryParams,
      BLOB_CONFIG
    ),
  postYardiCharges: (partnerId, data) =>
    post(`/admin/v1/partners/${partnerId}/post_yardi_charges`, data),
  bypassIncomeVerification: ({ verificationId }) =>
    post(`/admin/v1/verifications/${verificationId}/enable_bypass`),
  resubmitApplicant: (applicantId) =>
    post(`/admin/v1/applicants/${applicantId}/resubmit`),
  restartApplicant: (applicantId) =>
    post(`/admin/v1/applicants/${applicantId}/restart`),
  resubmitApplication: (applicationId) =>
    post(`/admin/v1/applications/${applicationId}/resubmit`),
  restartApplication: (applicationId) =>
    post(`/admin/v1/applications/${applicationId}/restart`),
  changeApplicationUnit: (applicationId, data) =>
    patch(`/admin/v1/applications/${applicationId}/unit`, data),
  patchPartnerUser: (partnerUserId, payload) =>
    patch(`/admin/v1/partner_users/${partnerUserId}`, payload),
  getPartnerUser: (partnerUserId) =>
    get(`/admin/v1/partner_users/${partnerUserId}`),
  getPartnerUserOtps: (partnerUserId) =>
    get(`/admin/v1/partner_users/${partnerUserId}/otps`),
  sendPartnerUserOtp: (partnerUserId) =>
    post(`/admin/v1/partner_users/${partnerUserId}/otps`),
  restartVerifiedIncomeVerification: ({ verificationId }) =>
    post(`/admin/v1/verifications/${verificationId}/restart`),
  changeApplicantData: (applicantId, payload) =>
    patch(`/admin/v1/applicants/${applicantId}`, payload),
  getReport: (applicantId) => get(`/admin/v1/applicants/${applicantId}/report`),
  getApplicantBillingActivities: (applicantId) =>
    get(`/admin/v1/applicants/${applicantId}/billing_activities`),

  getPmsRequestLogs: (data) => get("/admin/v1/pms_request_logs", data),

  rerunPmsRequest: (data) =>
    post(`/admin/v1/pms_request_logs/${data.id}/rerun`, data),
  removeCriminalReport: ({ verification_id, criminal_type, offense_id }) =>
    post(
      `/admin/v1/verifications/${verification_id}/criminal_report/${criminal_type}/${offense_id}/remove`
    ),
  getPartnerExclusions: (partnerId, data) =>
    get(`/admin/v1/partners/${partnerId}/exclusions`, data),
  createExclusion: (partnerId, data) =>
    post(`/admin/v1/partners/${partnerId}/exclusions`, data),
  postPreOptOutUpload: (partnerId, data) =>
    post(`/admin/v1/partners/${partnerId}/exclusions/csv`, data),
  deleteExclusion: (partnerId, exclusionId) =>
    del(`/admin/v1/partners/${partnerId}/exclusions/${exclusionId}`),
  getUniversalExclusions: (data) => get(`/admin/v1/universal_exclusions`, data),
  createUniversalExclusion: (data) =>
    post(`/admin/v1/universal_exclusions`, data),
  uploadUniversalExclusionsCsv: (data) =>
    post(`/admin/v1/universal_exclusions/csv`, data),
  deleteUniversalExclusion: (exclusionId) =>
    del(`/admin/v1/universal_exclusions/${exclusionId}`),
  getPmsCharges: (partnerId, data) =>
    get(`/admin/v1/partners/${partnerId}/pms_charges`, data),
  getPmsChargeDetails: (partnerId, chargeId) =>
    get(`/admin/v1/partners/${partnerId}/pms_charges/${chargeId}`),
  rerunPmsCharge: (partnerId, chargeId) =>
    post(`/admin/v1/partners/${partnerId}/pms_charges/${chargeId}/rerun`),
  reversePmsCharge: (partnerId, chargeId) =>
    post(`/admin/v1/partners/${partnerId}/pms_charges/${chargeId}/reverse`),
  getAppfolioGlAccounts: (partnerId) =>
    get(`/admin/v1/partners/${partnerId}/appfolio_gl_accounts`),
  createAppfolioCharges: (partnerId, charges) =>
    post(`/admin/v1/partners/${partnerId}/post_rent_reporting_charges`, {
      charges
    }),
  getFailedPmsChargesCsv: (partnerId, chargeIds) =>
    get(`/admin/v1/partners/${partnerId}/pms_charges/failed_csv`, {
      chargeIds: chargeIds?.join(",")
    }),
  fillCustomerIdentityViaCsv: (data) =>
    post(`/admin/v1/fill_customer_identity_via_csv`, data),
  getMcpToken: () => post(`/admin/v1/mcp/auth`),
  createBulkUpload: (data) => post("/admin/v1/bulk_uploads", data),
  listBulkUploads: (data) => get("/admin/v1/bulk_uploads", data),
  getBulkUploadStatus: (id) => get(`/admin/v1/bulk_uploads/${id}/status`),
  getIdentities: (data) => get("/admin/v1/identities", data),
  getIdentity: (data) => get(`/admin/v1/identities/${data.id}`, data),
  getCreditMonitoring: (data) =>
    get(`/admin/v1/credit_monitorings/${data.id}`, data),

  // Aggregated Payments
  getStatementsForAggregation: (data) =>
    get("/admin/v1/aggregated_payments/statements_for_aggregation", data),
  previewAggregatedPayments: (data) =>
    post("/admin/v1/aggregated_payments/preview", data),
  createAggregatedPayments: (data) =>
    post("/admin/v1/aggregated_payments", data),
  listAggregatedPayments: (data) => get("/admin/v1/aggregated_payments", data),
  getAggregatedPayment: (data) =>
    get(`/admin/v1/aggregated_payments/${data.id}`, data),
  confirmAggregatedPayment: ({ id, ...body }) =>
    post(
      `/admin/v1/aggregated_payments/${encodeURIComponent(id)}/confirm`,
      body
    ),
  retryAggregatedPayment: ({ id, ...body }) =>
    post(`/admin/v1/aggregated_payments/${encodeURIComponent(id)}/retry`, body),
  cancelAggregatedPayment: ({ id, ...body }) =>
    post(
      `/admin/v1/aggregated_payments/${encodeURIComponent(id)}/cancel`,
      body
    ),
  batchConfirmAggregatedPayments: (data) =>
    post("/admin/v1/aggregated_payments/batch_confirm", data),

  // Boom Report Charges
  getBoomReportCharges: (data) =>
    get("/admin/v1/billing/boom_report_charges", data),

  // Enrollment Case Partner Stats
  getEnrollmentCasePartnerStats: () =>
    get("/admin/v1/enrollment_case_partner_stats"),
  getMultipleActiveRaes: () =>
    get("/admin/v1/enrollment_case_partner_stats/multiple_active_raes"),

  // Lead Imports
  listLeadImports: ({ partnerId, ...params }) =>
    get(`/admin/v1/partners/${partnerId}/lead_imports`, params),
  getLeadImport: ({ partnerId, id }) =>
    get(`/admin/v1/partners/${partnerId}/lead_imports/${id}`),
  createLeadImport: ({ partnerId, formData }) =>
    post(`/admin/v1/partners/${partnerId}/lead_imports`, formData),
  updateLeadImportMapping: ({ partnerId, id, mapping }) =>
    patch(`/admin/v1/partners/${partnerId}/lead_imports/${id}/mapping`, {
      mapping
    }),
  commitLeadImport: ({ partnerId, id }) =>
    post(`/admin/v1/partners/${partnerId}/lead_imports/${id}/commit`),
  cancelLeadImport: ({ partnerId, id }) =>
    post(`/admin/v1/partners/${partnerId}/lead_imports/${id}/cancel`),

  // Phone Numbers
  getPartnerPhoneNumbers: ({ id }) =>
    get(`/admin/v1/partners/${id}/provisioned_phone_numbers`),
  provisionPartnerPhoneNumber: ({ id, ...data }) =>
    post(`/admin/v1/partners/${id}/provisioned_phone_numbers/provision`, data),
  approvePhoneNumberRequest: ({ id, phoneId }) =>
    post(
      `/admin/v1/partners/${id}/provisioned_phone_numbers/${phoneId}/approve`
    ),
  rejectPhoneNumberRequest: ({ id, phoneId }) =>
    post(
      `/admin/v1/partners/${id}/provisioned_phone_numbers/${phoneId}/reject`
    ),
  retryPhoneNumberProvision: ({ id, phoneId }) =>
    post(`/admin/v1/partners/${id}/provisioned_phone_numbers/${phoneId}/retry`),
  updatePartnerPhoneNumber: ({ id, phoneId, ...data }) =>
    patch(
      `/admin/v1/partners/${id}/provisioned_phone_numbers/${phoneId}`,
      data
    ),
  deletePartnerPhoneNumber: ({ id, phoneId }) =>
    del(`/admin/v1/partners/${id}/provisioned_phone_numbers/${phoneId}`),

  // Phone Numbers — cross-partner
  getPendingPhoneRequests: (params) =>
    get("/admin/v1/provisioned_phone_number_requests", params),

  // Phone Numbers — all (cross-partner, paginated)
  getAllPhoneNumbers: (params) =>
    get("/admin/v1/provisioned_phone_numbers", params),

  // Phone Numbers — local records (boompay-api DB, with webhook URLs)
  updateLocalPhoneNumber: ({ id, localId, ...data }) =>
    patch(
      `/admin/v1/partners/${id}/provisioned_phone_numbers/local/${localId}`,
      data
    ),
  deleteLocalPhoneNumber: ({ id, localId }) =>
    del(`/admin/v1/partners/${id}/provisioned_phone_numbers/local/${localId}`),
  syncPhoneNumbers: ({ id, phone_numbers }) =>
    post(`/admin/v1/partners/${id}/provisioned_phone_numbers/local/sync`, {
      phone_numbers
    }),
  importPhoneNumbersFromTwilio: ({ id, phone_numbers }) =>
    post(
      `/admin/v1/partners/${id}/provisioned_phone_numbers/local/import_from_twilio`,
      { phone_numbers }
    ),
  syncTwilioConfig: ({ id, localId }) =>
    post(
      `/admin/v1/partners/${id}/provisioned_phone_numbers/local/${localId}/sync_twilio_config`
    ),

  // Applicant Alerts
  getApplicantAlerts: (data) => get("/admin/v1/applicant_alerts", data),
  getApplicantAlert: (id) => get(`/admin/v1/applicant_alerts/${id}`),
  submitApplicantAlertAction: (id, data) =>
    post(`/admin/v1/applicant_alerts/${id}/action`, data),
  updateApplicantAlertAction: (id, data) =>
    put(`/admin/v1/applicant_alerts/${id}/action`, data),
  requestActionToken: (id, data) =>
    post(`/admin/v1/applicant_alerts/${id}/action_token`, data),

  // Workflow Analytics
  getWorkflowAnalyticsSummary: (params) =>
    get("/admin/v1/boom_crm/workflow_analytics/summary", params),
  getWorkflowAnalyticsDaily: (params) =>
    get("/admin/v1/boom_crm/workflow_analytics/daily", params),
  getWorkflowAnalyticsByTriggerType: (params) =>
    get("/admin/v1/boom_crm/workflow_analytics/by_trigger_type", params),
  getWorkflowAnalyticsByPartner: (params) =>
    get("/admin/v1/boom_crm/workflow_analytics/by_partner", params),
  getWorkflowAnalyticsByWorkflow: (params) =>
    get("/admin/v1/boom_crm/workflow_analytics/by_workflow", params),

  // Workflow Execution Log
  getWorkflowExecutionLog: (params) =>
    get("/admin/v1/boom_crm/workflow_analytics/executions", params),
  getWorkflowExecutionDetail: (executionId, params) =>
    get(
      `/admin/v1/boom_crm/workflow_analytics/executions/${executionId}`,
      params
    ),

  // Schedule Health
  getScheduleHealth: () =>
    get("/admin/v1/boom_crm/workflow_analytics/schedule_health"),
  postScheduleSync: () =>
    post("/admin/v1/boom_crm/workflow_analytics/schedule_sync"),
  postScheduleSyncWorkflow: (workflowId) =>
    post(`/admin/v1/boom_crm/workflow_analytics/schedule_sync/${workflowId}`),

  // ============================================================
  // BSS dashboard — proxied through /admin/v1/bss/*
  // to the shipping-service /api/admin/* endpoints.
  // ============================================================
  bssRuns: (params) => get("/admin/v1/bss/runs", params),
  bssRun: (id) => get(`/admin/v1/bss/runs/${encodeURIComponent(id)}`),
  bssCreateRun: (data) => post("/admin/v1/bss/runs", data),
  bssCancelRun: (id) =>
    post(`/admin/v1/bss/runs/${encodeURIComponent(id)}/cancel`),
  bssApproveRun: (id) =>
    post(`/admin/v1/bss/runs/${encodeURIComponent(id)}/approve`),
  bssRetryRun: ({ id, mode }) =>
    post(
      `/admin/v1/bss/runs/${encodeURIComponent(id)}/retry`,
      {},
      mode ? { params: { mode } } : undefined
    ),
  bssGroup: (id) => get(`/admin/v1/bss/groups/${encodeURIComponent(id)}`),
  bssTickets: (params) => get("/admin/v1/bss/tickets", params),
  bssTicket: (key) => get(`/admin/v1/bss/tickets/${encodeURIComponent(key)}`),
  bssDeployTicket: ({ key, ...body }) =>
    post(`/admin/v1/bss/tickets/${encodeURIComponent(key)}/deploy`, body),
  // Manually file a PR under a ticket key (an existing Jira ticket OR a new
  // QA-made group). `pr` is a GitHub PR URL or owner/repo#number. Used when
  // auto-adoption mis-keyed or missed a PR.
  bssAssignPr: (key, pr) =>
    post(`/admin/v1/bss/tickets/${encodeURIComponent(key)}/assign-pr`, { pr }),
  // QA impact analysis — manual Generate / Re-generate button on the Ticket
  // detail page. Fire-and-forget: 202 once enqueued; dashboard polls the
  // ticket GET (which embeds `impactAnalysis`) for the result.
  // `force` re-rolls even when the PR set is unchanged. Un-forced, the server
  // returns 409 { error: "pr_set_unchanged" } so the UI can confirm before
  // spending an LLM run on identical inputs.
  bssImpactAnalysisGenerate: (key, { force = false } = {}) =>
    post(
      `/admin/v1/bss/tickets/${encodeURIComponent(key)}/impact-analysis${
        force ? "?force=true" : ""
      }`,
      {}
    ),
  // The Investigator — bug-ticket findings. GET the latest investigation;
  // POST to (re-)run it (fire-and-forget 202; poll the ticket GET, which
  // embeds `bugInvestigation`, for the result).
  bssGetInvestigation: (key) =>
    get(`/admin/v1/bss/tickets/${encodeURIComponent(key)}/investigation`),
  bssInvestigate: (key) =>
    post(`/admin/v1/bss/tickets/${encodeURIComponent(key)}/investigation`, {}),
  // Turn a reviewed investigation into a ready-to-review fix PR. `force` skips
  // the "a fix PR already exists" guard (after the operator confirms).
  bssImplementInvestigation: (key, force = false) =>
    post(
      `/admin/v1/bss/tickets/${encodeURIComponent(key)}/investigation/implement`,
      { force }
    ),
  // QA test-session worksheet — interactive checklist QA fills out per
  // ticket (Pass/Fail/Blocked/Skip + observation + evidence + retest).
  // Seeded from the latest impact analysis on first GET; mutations PATCH
  // one item at a time so concurrent edits don't clobber wholesale.
  bssTestSession: (key) =>
    get(`/admin/v1/bss/tickets/${encodeURIComponent(key)}/test-session`),
  bssTestSessionPatchItem: ({ key, itemId, ...body }) =>
    patch(
      `/admin/v1/bss/tickets/${encodeURIComponent(key)}/test-session/items/${encodeURIComponent(itemId)}`,
      body
    ),
  bssTestSessionReset: (key) =>
    del(`/admin/v1/bss/tickets/${encodeURIComponent(key)}/test-session`),
  bssGroupDeploy: ({ id, ...body }) =>
    post(`/admin/v1/bss/groups/${encodeURIComponent(id)}/deploy`, body),
  // A PR's "Affected Products" checklist -> deploy app keys, for preselecting
  // the apps in the deploy modal (boompay-client). repo = "owner/repo".
  bssPrAffectedApps: (repo, pr) =>
    get("/admin/v1/bss/pr-affected-apps", { repo, pr }),
  bssStats: (params) => get("/admin/v1/bss/stats", params),
  bssReviewStats: (params) => get("/admin/v1/bss/review-stats", params),
  bssStatsExport: (params) =>
    // The shared instance appends `humps.camelizeKeys` to transformResponse,
    // which JSON-parses + re-stringifies the body and corrupts the CSV Blob
    // into "[object Object]". Override transformResponse for this request to
    // axios' raw default (identity for blobs) so the real CSV bytes survive.
    get("/admin/v1/bss/stats/export", params, {
      responseType: "blob",
      transformResponse: axios.defaults.transformResponse
    }),
  bssSettings: () => get("/admin/v1/bss/settings"),
  bssUpdateSettings: (data) => patch("/admin/v1/bss/settings", data),
  bssJiraIssue: (key) =>
    get(`/admin/v1/bss/jira/issues/${encodeURIComponent(key)}`),
  bssJiraSuggestions: (params) => get("/admin/v1/bss/jira/suggestions", params),
  bssPing: () => get("/admin/v1/bss/ping"),

  // GitHub PR-comment debounce queue. The webhook handler upserts trigger
  // rows; the scheduler dispatches once they've been quiet for the debounce
  // window. These endpoints let an operator see what's queued and bypass or
  // cancel a row that shouldn't fire.
  bssCommentTriggers: () => get("/admin/v1/bss/comment-triggers"),
  bssCancelCommentTrigger: (id) =>
    del(`/admin/v1/bss/comment-triggers/${encodeURIComponent(id)}`),
  bssDispatchCommentTriggerNow: (id) =>
    post(
      `/admin/v1/bss/comment-triggers/${encodeURIComponent(id)}/dispatch-now`
    ),

  // GitHub Actions failure debounce queue. Same shape as comment-triggers
  // but tracks failed workflow_run events instead of reviewer comments.
  bssCheckTriggers: () => get("/admin/v1/bss/check-triggers"),
  bssCancelCheckTrigger: (id) =>
    del(`/admin/v1/bss/check-triggers/${encodeURIComponent(id)}`),
  bssDispatchCheckTriggerNow: (id) =>
    post(`/admin/v1/bss/check-triggers/${encodeURIComponent(id)}/dispatch-now`),

  // Registry repos — authoritative list for the Add-to-brain repo select.
  bssRepos: () => get("/admin/v1/bss/repos"),
  // Browser-QA verification — manual trigger (requires an analyzed IA),
  // latest per-item execution verdicts, and evidence-video blob fetch (the
  // filename rides as ?file= because Grape eats path-segment extensions).
  bssBrowserVerifyTrigger: (key, body) =>
    post(
      `/admin/v1/bss/tickets/${encodeURIComponent(key)}/browser-verify`,
      body || {}
    ),
  bssBrowserVerifyStatus: (key) =>
    get(`/admin/v1/bss/tickets/${encodeURIComponent(key)}/browser-verify`),
  bssQaArtifact: (runId, file) =>
    // transformResponse override for the same reason as bssStatsExport: the
    // shared instance's camelizeKeys transform reduces a Blob (no own
    // enumerable keys) to `{}`, which makes createObjectURL throw. Raw
    // default = identity for blobs, so the video bytes survive.
    get(
      `/admin/v1/bss/qa-artifacts/${encodeURIComponent(runId)}`,
      { file },
      {
        responseType: "blob",
        transformResponse: axios.defaults.transformResponse
      }
    ),
  // Release train — pre-cut preview (per-repo labeled-PR counts) + trigger.
  bssReleaseCutPreview: () => get("/admin/v1/bss/release-cuts/preview"),
  bssReleaseCutTrigger: () => post("/admin/v1/bss/release-cuts", {}),
  // Release smoke/regression runner — DB-authored flows (CRUD), per-app login
  // config, run-modal metadata, the manual trigger, and the grouped report.
  bssQaFlows: (params) => get("/admin/v1/bss/qa-flows", params),
  bssQaFlowCreate: (fields) => post("/admin/v1/bss/qa-flows", fields),
  bssQaFlowUpdate: (id, fields) =>
    patch(`/admin/v1/bss/qa-flows/${encodeURIComponent(id)}`, fields),
  // Import an IA test case into a pending release flow (create-if-absent —
  // re-importing the same case returns the existing flow, never clobbers).
  bssQaFlowImport: ({ issueKey, stableKey }) =>
    post("/admin/v1/bss/qa-flows/import", { issueKey, stableKey }),
  // Publish a pending (IA-imported) flow: assign its app + cleaned tags /
  // side-effects, flipping status pending -> published (runnable).
  bssQaFlowPublish: (id, fields) =>
    post(`/admin/v1/bss/qa-flows/${encodeURIComponent(id)}/publish`, fields),
  bssQaAppLogins: () => get("/admin/v1/bss/qa-app-logins"),
  bssQaAppLoginUpsert: (appKey, fields) =>
    patch(`/admin/v1/bss/qa-app-logins/${encodeURIComponent(appKey)}`, fields),
  bssReleaseMeta: () => get("/admin/v1/bss/release-meta"),
  bssReleaseTrigger: (fields) => post("/admin/v1/bss/release-tests", fields),
  bssReleaseReport: (releaseKey) =>
    get(`/admin/v1/bss/release-tests/${encodeURIComponent(releaseKey)}`),
  // Brain — per-repo accumulating memory (Phase 0: list + curate).
  bssBrain: (params) => get("/admin/v1/bss/brain", params),
  // Manually add a brain entry (hand-authored knowledge — e.g. a PLAYBOOK).
  bssBrainCreate: (fields) => post("/admin/v1/bss/brain", fields),
  bssBrainSetStatus: (id, status) =>
    post(`/admin/v1/bss/brain/${encodeURIComponent(id)}/status`, {
      status
    }),
  bssBrainEdit: (id, fields) =>
    patch(`/admin/v1/bss/brain/${encodeURIComponent(id)}`, fields),

  // Knowledge — the /bss explain Q&A store (list / delete / force re-verify).
  bssKnowledge: () => get("/admin/v1/bss/knowledge"),
  bssKnowledgeDelete: (id) =>
    del(`/admin/v1/bss/knowledge/${encodeURIComponent(id)}`),
  bssKnowledgeReverify: () => post("/admin/v1/bss/knowledge/reverify", {}),

  // Rejections — PRs we did NOT adopt / review, with reason.
  bssRejections: (params) => get("/admin/v1/bss/rejections", params),

  // Force-review escape hatch — used by the ticket detail page's repo lane
  // when a webhook was lost (e.g. SS redeploy during label-add) and the user
  // needs to manually retrigger the review. Bypasses head-SHA dedup + per-PR
  // loop guard server-side via `force: true`.
  bssForceReview: ({ repoId, prNumber }) =>
    post("/admin/v1/bss/reviews/force", { repoId, prNumber })
};

export default { ...apiBase, ...api, instance };
